import type { PutWidgetConfigItem } from '@mummssoftware/utils';
import { renumberPatientDashboardWidgetMenuOrder } from './normalizeWidgetConfig';

/**
 * Patient-dashboard widgets with {@link mumms.WidgetConfig.doNotShow} (Facesheet, Forms).
 *
 * These rows stay in layout config (config-service / env) but are not dashboard grid tiles
 * or add-widget (+) menu entries — see {@link pickDoNotShowLayoutWidgets} and filtering in
 * `Dashboard/index.tsx`. Rows are kept so watermark settings and OpenShift access control
 * still apply.
 *
 * **Ribbon entry (patient dashboard only)** — not the grid:
 * | Widget    | Ribbon        | Component      |
 * |-----------|---------------|----------------|
 * | Facesheet | Upper sidebar | `FacesheetMenu` |
 * | Forms     | Lower sidebar | `FormListMenu`  |
 *
 * Both open via `OPEN_WIDGET` / `CLOSE_WIDGET` in global context. On save,
 * {@link buildPatientDashboardLayoutForSave} merges visible grid tiles with doNotShow rows
 * so PUT /api/widgets/config persists the full USER layout.
 */

/**
 * Visible grid tiles plus {@link mumms.WidgetConfig.doNotShow} rows (e.g. Forms, Facesheet)
 * for PUT /api/widgets/config. Matches dashboard save rules: same filters as grid, but
 * doNotShow widgets stay in the persisted USER layout even though they are not tiles.
 */
export function buildPatientDashboardLayoutForSave(
  dashboardGridConfig: mumms.WidgetConfig[],
  doNotShowWidgets: mumms.WidgetConfig[],
): mumms.WidgetConfig[] {
  const gridNames = new Set<mumms.ComponentName>(
    dashboardGridConfig
      .map((w) => w.componentName)
      .filter((n): n is mumms.ComponentName => Boolean(n)),
  );
  const extras = doNotShowWidgets.filter(
    (w) => w.componentName && !gridNames.has(w.componentName),
  );
  return renumberPatientDashboardWidgetMenuOrder([
    ...dashboardGridConfig.map((w) => ({ ...w })),
    ...extras.map((w) => ({ ...w })),
  ]);
}

function serializeJsonField(
  value:
    | mumms.WidgetConfig['printConfig']
    | mumms.IDGMeetingConfig
    | null
    | undefined,
): string | null | undefined {
  if (value == null) {
    return value === null ? null : undefined;
  }
  if (typeof value === 'string') {
    return value;
  }
  return JSON.stringify(value);
}

/**
 * Map saved patient dashboard layout rows to config-service PUT body.
 * Omits rows without componentName. Absent rows are deleted server-side (full layout replace).
 */
export function buildPutWidgetConfigPayload(
  layoutWidgets: mumms.WidgetConfig[],
  userId: string,
  dashboard: mumms.WidgetConfigDashboard,
): PutWidgetConfigItem[] {
  return layoutWidgets
    .filter((w): w is mumms.WidgetConfig & {
      componentName: mumms.ComponentName;
    } => Boolean(w.componentName))
    .map((w, index) => ({
      groupType: 'USER' as const,
      groupId: userId,
      dashboard,
      componentName: w.componentName,
      widgetMenuOrder: w.widgetMenuOrder ?? index,
      printMenuOrder: w.printMenuOrder,
      printable: w.printable,
      beta: w.beta,
      watermark: w.watermark,
      widgetName: w.widgetName,
      doNotShow: w.doNotShow,
      showForBins: w.showForBins,
      hideForBins: w.hideForBins,
      expandable: w.expandable,
      hasPrintRange: w.hasPrintRange,
      printConfig: serializeJsonField(w.printConfig),
      idgConfig: serializeJsonField(w.idgConfig),
    }));
}

/**
 * From post-filter {@link newConfig} (beta/agency, renumbered): rows excluded from the grid
 * but kept for watermark/access (Forms, Facesheet).
 */
export function pickDoNotShowLayoutWidgets(
  layoutConfig: mumms.WidgetConfig[],
): mumms.WidgetConfig[] {
  return layoutConfig.filter((w) => w.doNotShow);
}
